// Apolo IDE — preview client (sprint code-preview-engine, E2).
//
// Pure, framework-free wrappers over the Electron `window.apolo` bridge for the
// preview engine. Keeps every component/hook off `window.apolo` directly so the
// Dependency Rule holds and the renderer is testable by mocking ONE module.
// Outside Electron (web / playground / vitest) the bridge is absent → the
// wrappers are no-ops and the panel falls back to a plain <iframe> preview.

/** True when the Electron dev-server bridge is present. */
export function isElectron() {
  return typeof window !== 'undefined' && !!window.apolo?.devServer;
}

/** The previewed project's working dir: worktree first, then the session dir. */
export function resolvePreviewRoot(session) {
  if (!session) return null;
  return session.worktree_path || session.directory || null;
}

/** True for a localhost/127.0.0.1 URL (the only thing the <iframe> fallback can show). */
export function isLocalPreviewUrl(url) {
  return typeof url === 'string' && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(url.trim());
}

/**
 * Normalize a user-typed preview URL into a loadable one. The BrowserView host
 * (Electron) loads ANY http(s) URL — google.com, a localhost dev server, etc.
 * Adds a scheme when missing (http for localhost, https otherwise). Returns null
 * for empty/invalid input.
 */
export function normalizePreviewUrl(input) {
  if (typeof input !== 'string') return null;
  let u = input.trim();
  if (!u) return null;
  if (!/^https?:\/\//i.test(u)) {
    const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?($|\/)/i.test(u);
    u = (isLocal ? 'http://' : 'https://') + u;
  }
  try {
    const parsed = new URL(u);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return u.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

const ds = () => (typeof window !== 'undefined' ? window.apolo?.devServer : null);

export function startDevServer(params) {
  const api = ds();
  if (!api) return Promise.resolve({ ok: false, reason: 'no-electron' });
  return api.start(params);
}

export function stopDevServer(params) {
  const api = ds();
  if (!api) return Promise.resolve({ ok: false, reason: 'no-electron' });
  return api.stop(params);
}

export function devServerStatus(params) {
  const api = ds();
  if (!api) return Promise.resolve({ state: 'unsupported', url: null });
  return api.status(params);
}

/** Subscribe to streamed events; returns an unsubscribe (no-op outside Electron). */
export function onDevServerState(cb) {
  const api = ds();
  if (!api?.onState) return () => {};
  return api.onState(cb);
}

export function onDevServerOutput(cb) {
  const api = ds();
  if (!api?.onOutput) return () => {};
  return api.onOutput(cb);
}

/** Screenshot the preview (CDP first, then the embedded BrowserView). data URL | null. */
export async function capturePreviewScreenshot() {
  const api = pv();
  if (api?.screenshot) {
    try {
      const r = await api.screenshot();
      if (r?.dataUrl) return r.dataUrl;
    } catch {
      /* fall through to the embedded path */
    }
  }
  const embedded = typeof window !== 'undefined' ? window.apolo?.browser?.embedded : null;
  if (!embedded?.screenshot) return null;
  try {
    const res = await embedded.screenshot();
    if (res?.png_base64) return `data:image/png;base64,${res.png_base64}`;
    return null;
  } catch {
    return null;
  }
}

// ── E3/E4 preview DevTools (CDP) — wrappers over window.apolo.preview ─────────
// Present only in the packaged Electron app. Outside it every call is a safe
// no-op so the renderer/tests keep working with the <iframe> fallback.

/** True when the Electron preview DevTools (CDP) bridge is present. */
export function isPreviewCdp() {
  return typeof window !== 'undefined' && !!window.apolo?.preview;
}

const pv = () => (typeof window !== 'undefined' ? window.apolo?.preview : null);

export function previewAttach(params) {
  const api = pv();
  return api ? api.attach(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewBounds(params) {
  const api = pv();
  return api ? api.bounds(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewDetach() {
  const api = pv();
  return api ? api.detach() : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewSetDevice(params) {
  const api = pv();
  return api ? api.device(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewInspect(params) {
  const api = pv();
  return api ? api.inspect(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewTweak(params) {
  const api = pv();
  return api ? api.tweak(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function previewSetPick(params) {
  const api = pv();
  return api ? api.pick(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
// Modo Anotar (to-dos numerados no preview): {enabled} liga/desliga; {clear:true}
// remove os badges e zera o contador (pós-envio do prompt único).
export function previewSetAnnotate(params) {
  const api = pv();
  return api ? api.annotate(params) : Promise.resolve({ ok: false, reason: 'no-electron' });
}
export function onPreviewAnnotated(cb) {
  const api = pv();
  return api?.onAnnotated ? api.onAnnotated(cb) : () => {};
}
export function onPreviewConsole(cb) {
  const api = pv();
  return api?.onConsole ? api.onConsole(cb) : () => {};
}
export function onPreviewNetwork(cb) {
  const api = pv();
  return api?.onNetwork ? api.onNetwork(cb) : () => {};
}
export function onPreviewPicked(cb) {
  const api = pv();
  return api?.onPicked ? api.onPicked(cb) : () => {};
}
